import { computed, markRaw, ref, watch } from "vue";
import { defineStore } from "pinia";
import { useEventListener, watchDebounced } from "@vueuse/core";

import { captureException } from "@sentry/browser";

import { usePublicationStore } from "./publication";
import { ProgressionType } from "@/helpers/types";
import { Locator } from "@readium/shared";
import Device from "../wps/device";
import { useWpsStore } from "./wps";
import { deepToRaw } from "@/helpers/helperFunctions";

import fetch, { shouldRetryJsonFailure } from "../../modules/shared-utils/fetch";

export const useProgressionStore = defineStore("progression", () => {
  const publicationStore = usePublicationStore();
  const wps = useWpsStore();

  const loadingRemote = ref(false);
  let lastRemote: Locator | null = null;
  let lastLocal: Locator | null = null;
  watch(
    () => wps.client?.token.progressionEndpoint?.href,
    async (url) => {
      if (!url) {
        return null;
      }

      loadingRemote.value = true;

      try {
        let response: Response;
        try {
          response = await fetch(url, {
            method: "GET",
            shouldRetry: shouldRetryJsonFailure,
          });
        } catch (err) {
          if (navigator.onLine !== false) {
            captureException(err, {
              extra: { url },
            });
          }
          return null;
        }
        if (response.status === 404 || response.status === 204) {
          // No progression saved remotely yet
          return null;
        }
        if (!response.ok) {
          throw new Error("Failed to fetch progression");
        }

        const remote: ProgressionType = await response.json();
        const local = publicationStore.storage.progression;

        const localSavedAt = local?.modified
          ? Date.parse(local.modified) || 0
          : 0;
        const remoteSavedAt = remote?.modified
          ? Date.parse(remote.modified) || 0
          : 0;
        if (remoteSavedAt > localSavedAt) {
          lastLocal = lastRemote = remote.locator;
          publicationStore.storage.progression = remote;
        }
      } catch (err) {
        captureException(err);
      } finally {
        loadingRemote.value = false;
      }
    }
  );

  async function send(progression: ProgressionType);
  async function send(progression: ProgressionType, willUnload: boolean);
  async function send(
    progression: ProgressionType,
    willUnload: boolean = false
  ) {
    const url = wps.client?.token.progressionEndpoint?.href;
    if (!url) {
      return;
    }

    if (!progression?.locator) {
      return;
    }

    // NOTE: is there a better way to compare?
    if (JSON.stringify(lastRemote) === JSON.stringify(progression.locator)) {
      return;
    }

    const body = JSON.stringify(progression);
    const options: RequestInit = { method: "PUT", body };
    if (willUnload) {
      options.keepalive = true;
    }
    await fetch(url, options);
    lastRemote = progression.locator;
  }

  watchDebounced(() => publicationStore.storage.progression, send, {
    debounce: 5000,
    maxWait: 15000,
  });

  useEventListener(document, "visibilitychange", async () => {
    if (document.visibilityState === "hidden" || document.hidden) {
      await send(publicationStore.storage.progression);
    }
  });

  useEventListener(window, "beforeunload", async () => {
    await send(publicationStore.storage.progression, true);
  });

  async function pushLocation(locator: Locator) {
    if (JSON.stringify(locator) === JSON.stringify(lastLocal)) {
      return;
    }
    publicationStore.storage.progression = {
      locator,
      modified: new Date().toJSON(),
      device: {
        id: await Device.id,
        name: Device.name,
      },
    };
    lastLocal = locator;
  }

  const ready = computed(
    () =>
      publicationStore.ready &&
      (!wps.client?.token.progressionEndpoint?.href || !loadingRemote.value)
  );

  return {
    ready,
    lastKnownPosition: computed<Locator | null>(() =>
      ensureLocatorInstance(publicationStore.storage.progression?.locator)
    ),
    pushLocation,
  };
});

function ensureLocatorInstance(locator: Locator | unknown): Locator {
  if (!locator) {
    return null;
  }
  if (!(locator instanceof Locator)) {
    locator = Locator.deserialize(deepToRaw(locator));
  }
  return markRaw(locator as Locator);
}
